import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { StatusBadge } from "@/components/data/status-badge";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useOperacoesManejo, useUpdateOperacaoStatus, useOperacaoManejo } from "@/hooks/use-manejo";
import { usePode } from "@/lib/permissoes";
import type { MnjOperacaoWithItens } from "@shared/schema";
import { grupoDaOperacao } from "@shared/manejo-hoje";
import {
  intervaloDaSemana, diasDaSemana, agruparPorDia, atrasadasDaSemana, semanaIsoDe,
} from "@shared/manejo-agenda";
import { hojeNaFazenda, somarDiasCivis } from "@/lib/data-fazenda";
import { OperacaoDetalhe } from "./operacao-detalhe";
import { NovaOperacaoDialog } from "./nova-operacao-dialog";
import { iconeDaCategoria, tituloDaOperacao } from "./card-operacao";
import { ChevronLeft, ChevronRight, Plus, AlertTriangle, CalendarRange } from "lucide-react";

// Guia SEMANA — spec §6.2.
//
// «Grade seg–sáb, uma coluna por dia, cards compactos, arrasto muda
// `data_prevista`. Domingo aparece só se tiver algo. É a tela de quem planeja;
// no celular vira lista agrupada por dia.»
//
// UM componente para os dois públicos, com o mesmo dado: `md:` decide se a
// semana é grade (mouse, arrasto) ou lista empilhada (polegar, sem arrasto).
// Duas telas separadas divergiriam no primeiro ajuste.
//
// 🔴 As regras (janela da semana, agrupamento, quais atrasadas sobem para a
// faixa do topo) NÃO moram aqui: são `shared/manejo-agenda.ts`, puras e
// testadas. O repo não tem jsdom.

const NOME_DIA = ["seg", "ter", "qua", "qui", "sex", "sáb", "dom"];

function rotuloCurto(data: string): string {
  return new Date(data + "T00:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

/** "24–29 ago" — e "28 ago–2 set" quando a semana atravessa o mês. */
function rotuloIntervalo(inicio: string, fim: string): string {
  const d1 = new Date(inicio + "T00:00:00");
  const d2 = new Date(fim + "T00:00:00");
  const mes = (d: Date) => d.toLocaleDateString("pt-BR", { month: "short" }).replace(".", "");
  const dia = (d: Date) => String(d.getDate());
  return inicio.slice(0, 7) === fim.slice(0, 7)
    ? `${dia(d1)}–${dia(d2)} ${mes(d2)}`
    : `${dia(d1)} ${mes(d1)}–${dia(d2)} ${mes(d2)}`;
}

export interface SemanaOperacoesProps {
  /** Abre "Nova operação" já com a data da coluna (o `+` do cabeçalho). */
  onNovaOperacaoEm: (data: string) => void;
}

export function SemanaOperacoes({ onNovaOperacaoEm }: SemanaOperacoesProps) {
  const { toast } = useToast();
  const hoje = hojeNaFazenda();
  // Âncora da semana visível. Começa em `hojeNaFazenda()` — nunca `new Date()`:
  // às 21h de um domingo na Bahia o UTC já é segunda, e o quadro abriria na
  // semana seguinte (o bug que o kanban teve).
  const [ancora, setAncora] = useState<string>(hoje);
  const [detalheId, setDetalheId] = useState<string | null>(null);
  // Editar = criar: o diálogo único, em modo editar.
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const { data: editandoOp } = useOperacaoManejo(editandoId);
  const podePlanejar = usePode("manejo.planejar");
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [alvoSolta, setAlvoSolta] = useState<string | null>(null);

  const { inicio, fim } = useMemo(() => intervaloDaSemana(ancora), [ancora]);

  // Uma consulta só, sem piso de data: precisamos da semana E das atrasadas de
  // antes dela para a faixa do topo. `ate: fim` é o teto; o backlog (data nula)
  // não satisfaz `<=` e fica de fora por construção.
  const { data: operacoes = [], isLoading } = useOperacoesManejo({ ate: fim });
  const mover = useUpdateOperacaoStatus();

  const daSemana = useMemo(
    () => operacoes.filter(op => op.dataPrevista !== null && op.dataPrevista >= inicio && op.dataPrevista <= fim),
    [operacoes, inicio, fim],
  );
  const temDomingo = useMemo(
    () => daSemana.some(op => op.dataPrevista === somarDiasCivis(inicio, 6)),
    [daSemana, inicio],
  );
  const dias = useMemo(
    () => diasDaSemana(ancora, temDomingo).map(d => ({ ...d, ehHoje: d.data === hoje })),
    [ancora, temDomingo, hoje],
  );
  const grupos = useMemo(() => agruparPorDia(daSemana, dias), [daSemana, dias]);
  const atrasadas = useMemo(() => atrasadasDaSemana(operacoes, inicio, hoje), [operacoes, inicio, hoje]);

  const ehSemanaCorrente = hoje >= inicio && hoje <= fim;

  /**
   * Arrastar solta a operação noutro dia. Reusa o `PATCH /status` que já existe
   * mandando `dataPrevista` — ele já grava `dataAnterior`/`dataNova` no payload
   * do evento. Nenhum endpoint novo (o servidor já fazia tudo isto).
   */
  const soltarEm = async (data: string) => {
    const id = arrastando;
    setArrastando(null);
    setAlvoSolta(null);
    if (!id) return;
    const op = operacoes.filter(o => o.id === id)[0];
    if (!op || op.dataPrevista === data) return;
    try {
      await mover.mutateAsync({ id, status: "planejada", dataPrevista: data });
      toast({ title: `Movida para ${rotuloCurto(data)}` });
    } catch (e: any) {
      toast({ title: "Não deu para mover", description: e?.message, variant: "destructive" });
    }
  };

  if (isLoading) return <PageSkeleton variante="cards" linhas={3} />;

  return (
    <div className="space-y-3">
      {/* Navegação da semana */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Button
            variant="outline" size="icon" className="h-11 w-11"
            onClick={() => setAncora(somarDiasCivis(inicio, -7))}
            aria-label="Semana anterior" data-testid="semana-anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline" size="icon" className="h-11 w-11"
            onClick={() => setAncora(somarDiasCivis(inicio, 7))}
            aria-label="Próxima semana" data-testid="semana-proxima"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="min-w-0 flex-1 text-center">
          <p className="truncate text-sm font-semibold" data-testid="semana-rotulo">
            Semana {semanaIsoDe(inicio)} · {rotuloIntervalo(inicio, fim)}
          </p>
        </div>
        <Button
          variant={ehSemanaCorrente ? "ghost" : "outline"}
          className="min-h-11 shrink-0"
          disabled={ehSemanaCorrente}
          onClick={() => setAncora(hoje)}
          data-testid="semana-hoje"
        >
          Hoje
        </Button>
      </div>

      {/* 🔴 Faixa das atrasadas, FORA da grade. Elas vêm de antes desta semana e
          não caberiam em coluna nenhuma — e sumir com elas ao navegar é como o
          trabalho se perde de vista. */}
      {atrasadas.length > 0 && (
        <section
          className="rounded-lg border border-erro/40 p-2.5 space-y-2"
          data-testid="faixa-atrasadas"
        >
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-erro">
            <AlertTriangle className="h-4 w-4" />
            Atrasadas de antes desta semana · {atrasadas.length}
          </h2>
          <div className="flex flex-col gap-1.5 md:flex-row md:flex-wrap">
            {atrasadas.map(op => (
              <CardCompacto
                key={op.id}
                operacao={op}
                atrasada
                onAbrir={() => setDetalheId(op.id)}
                onArrastar={() => setArrastando(op.id)}
                className="md:w-56"
              />
            ))}
          </div>
        </section>
      )}

      {/* ── Desktop: grade. Cada coluna rola sozinha; a faixa horizontal só
             aparece se as 6/7 colunas não couberem. ── */}
      <div className="hidden md:flex gap-2 overflow-x-auto pb-2" data-testid="semana-grade">
        {grupos.map(({ dia, operacoes: doDia }) => (
          <div
            key={dia.data}
            onDragOver={e => { e.preventDefault(); setAlvoSolta(dia.data); }}
            onDragLeave={() => setAlvoSolta(a => (a === dia.data ? null : a))}
            onDrop={e => { e.preventDefault(); soltarEm(dia.data); }}
            className={cn(
              "flex min-w-[180px] flex-1 flex-col rounded-lg border bg-card transition-colors",
              dia.ehHoje && "border-primary",
              alvoSolta === dia.data && "bg-primary/5 border-primary",
            )}
            data-testid={`coluna-${dia.data}`}
          >
            <div className="flex items-center justify-between gap-1 border-b px-2 py-2">
              <span className="min-w-0 truncate text-sm">
                <span className={cn("font-semibold", dia.ehHoje && "text-primary")}>
                  {NOME_DIA[dia.diaSemana - 1]}
                </span>{" "}
                <span className="text-muted-foreground">{rotuloCurto(dia.data)}</span>
              </span>
              <span className="flex shrink-0 items-center gap-1">
                {doDia.length > 0 && (
                  <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums">{doDia.length}</span>
                )}
                {podePlanejar && (
                  <button
                    type="button"
                    onClick={() => onNovaOperacaoEm(dia.data)}
                    className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    aria-label={`Nova operação em ${dia.data}`}
                    data-testid={`novo-${dia.data}`}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                )}
              </span>
            </div>
            <div className="flex-1 space-y-1.5 p-1.5">
              {doDia.map(op => (
                <CardCompacto
                  key={op.id}
                  operacao={op}
                  atrasada={grupoDaOperacao(op, hoje) === "atrasada"}
                  onAbrir={() => setDetalheId(op.id)}
                  onArrastar={() => setArrastando(op.id)}
                />
              ))}
              {doDia.length === 0 && (
                <p className="py-4 text-center text-xs text-muted-foreground">—</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Celular: a mesma semana empilhada. Sem arrasto — para mudar a data, o
             caminho é o detalhe da operação. ── */}
      <div className="space-y-3 md:hidden" data-testid="semana-lista">
        {grupos.map(({ dia, operacoes: doDia }) => (
          <section key={dia.data}>
            {/* Cabeçalho fixo: rolando uma semana cheia, saber "que dia é este"
                sem voltar ao topo é o que torna a lista utilizável.
                ⚠️ SEM `-mx-1`: a margem negativa fazia a seção ficar 4px mais
                larga que o pai (medido a 375px). A página não chegava a rolar
                porque o padding do container absorvia — mas é overflow real, e
                num container sem padding viraria barra horizontal. */}
            <div className={cn(
              "sticky top-0 z-10 flex items-center justify-between gap-2 bg-background py-1.5",
              dia.ehHoje && "text-primary",
            )}>
              <h2 className="text-sm font-semibold">
                {NOME_DIA[dia.diaSemana - 1]} {rotuloCurto(dia.data)}
                {dia.ehHoje && <span className="ml-1.5 font-normal text-muted-foreground">hoje</span>}
              </h2>
              <span className="flex items-center gap-1">
                {doDia.length > 0 && (
                  <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums">{doDia.length}</span>
                )}
                {podePlanejar && (
                  <button
                    type="button"
                    onClick={() => onNovaOperacaoEm(dia.data)}
                    className="rounded p-2 text-muted-foreground hover:bg-muted"
                    aria-label={`Nova operação em ${dia.data}`}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                )}
              </span>
            </div>
            <div className="space-y-1.5 pt-1">
              {doDia.map(op => (
                <CardCompacto
                  key={op.id}
                  operacao={op}
                  atrasada={grupoDaOperacao(op, hoje) === "atrasada"}
                  onAbrir={() => setDetalheId(op.id)}
                />
              ))}
              {doDia.length === 0 && (
                <p className="py-1 text-xs text-muted-foreground">Nada planejado.</p>
              )}
            </div>
          </section>
        ))}
      </div>

      {daSemana.length === 0 && atrasadas.length === 0 && (
        <p className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
          <CalendarRange className="h-4 w-4" />
          Nenhuma operação nesta semana.
        </p>
      )}

      <NovaOperacaoDialog
        open={!!editandoId && !!editandoOp}
        onOpenChange={aberto => !aberto && setEditandoId(null)}
        operacao={editandoOp ?? null}
      />

      <OperacaoDetalhe
        operacaoId={detalheId}
        onOpenChange={aberto => !aberto && setDetalheId(null)}
        onEditar={podePlanejar ? (op => { setDetalheId(null); setEditandoId(op.id); }) : undefined}
        onIniciar={() => setDetalheId(null)}
        onConcluir={() => setDetalheId(null)}
        onReabrir={() => setDetalheId(null)}
        onCancelar={() => setDetalheId(null)}
        onComentar={() => {}}
      />
    </div>
  );
}

/**
 * Card compacto da grade: ícone da categoria, título, setores, quem está
 * atribuído. Sem botão de ação — na Semana se PLANEJA; iniciar e concluir são da
 * tela Hoje, e um botão pequeno aqui convidaria ao toque errado.
 */
function CardCompacto({
  operacao, atrasada, onAbrir, onArrastar, className,
}: {
  operacao: MnjOperacaoWithItens;
  atrasada?: boolean;
  onAbrir: () => void;
  onArrastar?: () => void;
  className?: string;
}) {
  const Icone = iconeDaCategoria(operacao.atividade?.categoria);
  const concluida = operacao.status === "concluida";
  const executores = operacao.executores.map(e => e.funcionario.apelido ?? e.funcionario.nome);
  const titulo = tituloDaOperacao(operacao);
  // H: o título já traz "· B4, C4"; os chips só voltam quando ele resume ("+N") e os
  // setores deixaram de caber nele.
  const tituloResumido = /\+\d+$/.test(titulo);

  return (
    <div
      draggable={!!onArrastar}
      onDragStart={onArrastar}
      onClick={onAbrir}
      className={cn(
        "cursor-pointer rounded-md border bg-background p-2 text-left transition-colors hover:bg-muted/40",
        onArrastar && "md:cursor-grab md:active:cursor-grabbing",
        atrasada && "border-erro/40",
        concluida && "opacity-60",
        className,
      )}
      data-testid={`card-semana-${operacao.id}`}
    >
      <div className="flex items-start gap-1.5">
        <Icone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 line-clamp-2 text-base font-medium leading-snug sm:text-sm">{titulo}</span>
        {operacao.prioridade === "alta" && !concluida && <StatusBadge tom="atencao">alta</StatusBadge>}
      </div>
      {tituloResumido && operacao.setores.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {operacao.setores.map(s => (
            <Badge key={s.setorId} variant="outline" className="font-normal">{s.setor.nome}</Badge>
          ))}
        </div>
      )}
      <div className="mt-1 flex items-center justify-between gap-1.5">
        <span className="min-w-0 truncate text-sm text-muted-foreground sm:text-xs">
          {executores.length > 0 ? executores.join(", ") : "sem executor"}
        </span>
        {concluida && <StatusBadge tom="sucesso">feita</StatusBadge>}
      </div>
    </div>
  );
}
